import React from 'react';
import moment from 'moment';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { useForm, Controller } from 'react-hook-form';

import ImageComponent from 'shared/ImageComponent';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import useComments from '../../hooks/useComments';

/**
 * Component for rendering an individual comment with user info, content, and action buttons
 *
 * @param {Object} props - The component props
 * @param {Object} props.comment - The comment object containing id, text, user info, and time
 * @param {Function} props.onEdit - Function to call when edit button is clicked
 * @param {Function} props.onDelete - Function to call when delete button is clicked
 * @param {boolean} props.isDeletingComment - Whether a comment is currently being deleted
 * @param {string|null} props.editingComment - ID of the comment currently being edited (if any)
 * @param {string} props.editCommentText - Current text in the edit field
 * @param {Function} props.onSave - Function to call when save button is clicked
 * @param {Function} props.onCancel - Function to call when cancel button is clicked
 * @param {Function} props.setEditCommentText - Function to update the edit text state
 * @returns {JSX.Element} The rendered comment item
 */
const CommentItem = ({
  comment,
  onEdit,
  onDelete,
  isDeletingComment,
  editingComment,
  editCommentText,
  onSave,
  onCancel,
  setEditCommentText,
}) => {
  return (
    <div className="d-flex">
      <div>
        <ImageComponent
          filePath={comment.user.avatar}
          fileName={comment.user.name}
          className="me-2"
          avatarSize="small"
          style={{
            backgroundColor: '#2196F3',
            color: '#ffffff',
          }}
        />
      </div>
      <div className="flex-grow-1">
        <div className="d-flex justify-content-between align-items-center">
          <div className="text-bold F-size14">{comment.user.name}</div>
          {comment.editable && (
            <div>
              <i
                className={`pi pi-pencil pointer me-2 ${editingComment === comment.id ? 'text-primary' : ''}`}
                onClick={() => onEdit(comment.id, comment.text)}
              ></i>
              <i
                className={`pi pi-trash pointer ${isDeletingComment ? 'text-muted' : 'text-danger'}`}
                onClick={() => !isDeletingComment && onDelete(comment.id)}
              ></i>
            </div>
          )}
        </div>
        <div className="F-size12 text-muted">{moment(comment.time).format('MM/DD/YYYY hh:mm A')}</div>
        {editingComment === comment.id ? (
          <div className="mt-2">
            <TextSnippetWrapper
              type="textarea"
              rows="3"
              className="w-100 input-shadow"
              value={editCommentText}
              onChange={(e) => setEditCommentText(e.target.value)}
            />
            <div className="d-flex justify-content-end mt-2">
              <Button className="p-button-text p-button-secondary me-2" onClick={onCancel} size="small">
                Cancel
              </Button>
              <Button className="p-button-secondary" onClick={() => onSave(comment.id)} size="small">
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="F-size13 text-dark comment-text-truncated"
            data-pr-tooltip={comment.text.length > 150 ? comment.text : null}
            data-pr-position="top"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {comment.text}
          </div>
        )}
        <Tooltip target=".comment-text-truncated" />
      </div>
    </div>
  );
};

/**
 * Form component for adding or editing comments using React Hook Form
 *
 * @param {Object} props - The component props
 * @param {Function} props.onSubmit - Function to call when the form is submitted
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting
 * @param {boolean} props.isEditing - Whether the form is in edit mode
 * @param {string} props.defaultValue - Default text value for the form
 * @param {string} props.buttonLabel - Label for the submit button
 * @returns {JSX.Element} The rendered form component
 */
const CommentForm = ({ onSubmit, isSubmitting, isEditing = false, defaultValue = '', buttonLabel = 'Add Comment' }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      comment: defaultValue,
    },
  });

  /**
   * Handles form submission and resets the form if not in edit mode
   *
   * @param {Object} data - The form data
   * @param {string} data.comment - The comment text
   */
  const submitForm = (data) => {
    onSubmit(data.comment);
    if (!isEditing) {
      reset({ comment: '' });
    }
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <Controller
        name="comment"
        control={control}
        rules={{ required: 'Comment cannot be empty' }}
        render={({ field }) => (
          <TextSnippetWrapper type="textarea" rows="3" className="w-100 input-shadow" placeholder="Add a comment..." {...field} />
        )}
      />
      {errors.comment && <small className="p-error">{errors.comment.message}</small>}
      <div className="d-flex justify-content-end mt-2">
        <Button
          className="p-button-secondary"
          type="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
          loadingIcon="pi pi-spin pi-spinner"
          label={buttonLabel}
        />
      </div>
    </form>
  );
};

/**
 * Main component for displaying and managing task comments
 * Includes a form for adding comments and a scrollable list of existing comments
 *
 * @param {Object} props - The component props
 * @param {string} props.taskId - The ID of the task to display comments for
 * @param {string|null} props.caseId - The ID of the case associated with the task
 * @param {Object} props.userContext - The user context containing user details
 * @returns {JSX.Element} The rendered comments section
 */
const CommentsSection = ({ taskId, caseId, userContext }) => {
  const {
    comments,
    isAddingComment,
    isLoadingComments,
    isLoadingMoreComments,
    isDeletingComment,
    editingComment,
    editCommentText,
    commentsListRef,
    setEditCommentText,
    addComment,
    startEditing,
    updateComment,
    cancelEditing,
    deleteComment,
    handleScroll,
  } = useComments(taskId, caseId, userContext);

  return (
    <div className="comments-section">
      <h5 className="modal-heading F-size16 text-bold">Comments ({comments.length})</h5>
      <hr />

      <div className="mb-3">
        <CommentForm onSubmit={addComment} isSubmitting={isAddingComment} />
      </div>

      <div
        className="comments-list thin-scrollbar"
        style={{
          maxHeight: '350px',
          overflowY: 'auto',
          paddingBottom: '10px',
        }}
        onScroll={handleScroll}
        ref={commentsListRef}
      >
        {isLoadingComments ? (
          <div className="d-flex justify-content-center p-3">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
          </div>
        ) : comments.length > 0 ? (
          <>
            {comments.map((comment, index) => (
              <div
                key={comment.id}
                className="comment-item border-bottom pb-2"
                style={{
                  marginBottom: index === comments.length - 1 ? '20px' : '8px',
                }}
              >
                <CommentItem
                  comment={comment}
                  onEdit={startEditing}
                  onDelete={deleteComment}
                  isDeletingComment={isDeletingComment}
                  editingComment={editingComment}
                  editCommentText={editCommentText}
                  setEditCommentText={setEditCommentText}
                  onSave={updateComment}
                  onCancel={cancelEditing}
                />
              </div>
            ))}
            {isLoadingMoreComments && (
              <div className="d-flex justify-content-center p-2">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }}></i>
              </div>
            )}
          </>
        ) : (
          <div className="text-center p-3 text-muted">No comments yet</div>
        )}
      </div>
    </div>
  );
};

export default CommentsSection;
